import queryString from "query-string";
import {
  AdvanceByReasonResponse,
  AdvanceByTravelerResponse,
  ByProductResponse,
  DimensionalResponse,
  PolicyByUnitResponse,
  PolicyComplianceResponse,
  SummaryResponse,
  TopViolationsResponse,
  TotalSpentResponse,
  TravelAnalyticsFilters,
} from "~/core/modules/Customer/pages/CustomerDashboardBIV2Page/types";
import { integrationsHubApi } from "~/infrastructure/api/integrationsHubClient";
import {
  AdvanceByTravelerQuery,
  DimensionalQuery,
  ITravelAnalyticsService,
  TopViolationsQuery,
} from "./ITravelAnalyticsService";

const PREFIX = "travel-analytics";

const buildUrl = (path: string, query: Record<string, unknown>): string =>
  queryString.stringifyUrl(
    {
      url: `${PREFIX}/${path}`,
      query: query as Record<string, string | number | boolean>,
    },
    { skipNull: true, skipEmptyString: true },
  );

const baseQuery = (filters: TravelAnalyticsFilters) => ({
  customerUuid: filters.customerUuid,
  fromYearMonth: filters.fromYearMonth,
  toYearMonth: filters.toYearMonth,
  productType: filters.productType,
});

export class TravelAnalyticsService implements ITravelAnalyticsService {
  async getSummary(filters: TravelAnalyticsFilters): Promise<SummaryResponse> {
    const url = buildUrl("summary", baseQuery(filters));
    return integrationsHubApi.get<SummaryResponse>(url).then(({ data }) => data);
  }

  async getTotalSpent(filters: TravelAnalyticsFilters): Promise<TotalSpentResponse> {
    const url = buildUrl("total-spent", baseQuery(filters));
    return integrationsHubApi.get<TotalSpentResponse>(url).then(({ data }) => data);
  }

  async getByProduct(filters: TravelAnalyticsFilters): Promise<ByProductResponse> {
    const url = buildUrl("by-product", baseQuery(filters));
    return integrationsHubApi.get<ByProductResponse>(url).then(({ data }) => data);
  }

  async getByCostCenter(query: DimensionalQuery): Promise<DimensionalResponse> {
    const url = buildUrl("by-cost-center", {
      ...baseQuery(query),
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 10,
      search: query.search,
    });
    return integrationsHubApi.get<DimensionalResponse>(url).then(({ data }) => data);
  }

  async getByUnit(filters: TravelAnalyticsFilters): Promise<DimensionalResponse> {
    const url = buildUrl("by-unit", baseQuery(filters));
    return integrationsHubApi.get<DimensionalResponse>(url).then(({ data }) => data);
  }

  async getPolicyCompliance(filters: TravelAnalyticsFilters): Promise<PolicyComplianceResponse> {
    const url = buildUrl("policy-compliance", baseQuery(filters));
    return integrationsHubApi.get<PolicyComplianceResponse>(url).then(({ data }) => data);
  }

  async getAdvanceByReason(filters: TravelAnalyticsFilters): Promise<AdvanceByReasonResponse> {
    const url = buildUrl("advance-by-reason", baseQuery(filters));
    return integrationsHubApi.get<AdvanceByReasonResponse>(url).then(({ data }) => data);
  }

  async getTopViolations(query: TopViolationsQuery): Promise<TopViolationsResponse> {
    const url = buildUrl("top-violations", {
      ...baseQuery(query),
      dimension: query.dimension,
      limit: query.limit ?? 30,
    });
    return integrationsHubApi.get<TopViolationsResponse>(url).then(({ data }) => data);
  }

  async getPolicyByUnit(filters: TravelAnalyticsFilters): Promise<PolicyByUnitResponse> {
    const url = buildUrl("policy-by-unit", baseQuery(filters));
    return integrationsHubApi.get<PolicyByUnitResponse>(url).then(({ data }) => data);
  }

  async getAdvanceByTraveler(query: AdvanceByTravelerQuery): Promise<AdvanceByTravelerResponse> {
    const url = buildUrl("advance-by-traveler", {
      ...baseQuery(query),
      limit: query.limit ?? 30,
    });
    return integrationsHubApi.get<AdvanceByTravelerResponse>(url).then(({ data }) => data);
  }

  async getByProject(query: DimensionalQuery): Promise<DimensionalResponse> {
    const url = buildUrl("by-project", {
      ...baseQuery(query),
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 10,
      search: query.search,
    });
    return integrationsHubApi.get<DimensionalResponse>(url).then(({ data }) => data);
  }
}
